import { Box, Typography, alpha, useTheme } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

// Removed terminal font to match dashboard aesthetic

interface IntelligenceIntroAnimationProps {
  onComplete: () => void;
}

export const IntelligenceIntroAnimation: React.FC<IntelligenceIntroAnimationProps> = ({ onComplete }) => {
  const theme = useTheme();
  const [phase, setPhase] = useState<'search' | 'synthesize' | 'monitor'>('search');

  useEffect(() => {
    const timer1 = setTimeout(() => setPhase('synthesize'), 2500);
    const timer2 = setTimeout(() => setPhase('monitor'), 5000);
    const timer3 = setTimeout(onComplete, 7500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onComplete]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: '#000',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <AnimatePresence mode="wait">
        {phase === 'search' && (
          <motion.div
            key="search"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box sx={{ position: 'relative', width: 300, height: 300 }}>
              {/* Scanning Grid Effect */}
              <motion.div
                style={{
                  position: 'absolute',
                  inset: 0,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: '50%',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 4, ease: "linear", repeat: Infinity }}
              />
              <motion.div
                style={{
                  position: 'absolute',
                  inset: 20,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: '50%',
                }}
                animate={{ rotate: -180 }}
                transition={{ duration: 6, ease: "linear", repeat: Infinity }}
              />
              
              {/* Nodes appearing */}
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  style={{
                    position: 'absolute',
                    width: 4,
                    height: 4,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '50%',
                    top: '50%',
                    left: '50%',
                  }}
                  animate={{
                    x: Math.cos(i * 45 * (Math.PI / 180)) * 100,
                    y: Math.sin(i * 45 * (Math.PI / 180)) * 100,
                    opacity: [0, 1, 0.5],
                    scale: [0, 1.5, 1],
                  }}
                  transition={{ delay: i * 0.1, duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
                />
              ))}
            </Box>
            <Typography variant="h6" sx={{ mt: 4, color: theme.palette.primary.main, letterSpacing: '0.1em', fontWeight: 600 }}>
              SCANNING GLOBAL SOURCES
            </Typography>
            <Typography variant="caption" sx={{ mt: 1, color: alpha('#fff', 0.5) }}>
              ANALYZING 2ND & 3RD ORDER IMPACTS
            </Typography>
          </motion.div>
        )}

        {phase === 'synthesize' && (
          <motion.div
            key="synthesize"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box sx={{ position: 'relative', width: 300, height: 300 }}>
               {/* Connecting Nodes */}
               <svg width="300" height="300" style={{ position: 'absolute', top: 0, left: 0 }}>
                 {[...Array(5)].map((_, i) => (
                   <motion.line
                     key={i}
                     x1="150"
                     y1="150"
                     x2={150 + Math.cos(i * 72 * (Math.PI / 180)) * 100}
                     y2={150 + Math.sin(i * 72 * (Math.PI / 180)) * 100}
                     stroke={theme.palette.secondary.main}
                     strokeWidth="2"
                     initial={{ pathLength: 0, opacity: 0 }}
                     animate={{ pathLength: 1, opacity: 1 }}
                     transition={{ duration: 1, delay: i * 0.1 }}
                   />
                 ))}
               </svg>
               
               <motion.div
                 style={{
                   position: 'absolute',
                   top: '50%',
                   left: '50%',
                   width: 60,
                   height: 60,
                   x: -30,
                   y: -30,
                   backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                   border: `2px solid ${theme.palette.secondary.main}`,
                   borderRadius: '50%',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                 }}
                 initial={{ scale: 0 }}
                 animate={{ scale: 1 }}
                 transition={{ type: "spring", stiffness: 200, damping: 10 }}
               >
                 <Box sx={{ width: 20, height: 20, bgcolor: theme.palette.secondary.main, borderRadius: '50%' }} />
               </motion.div>
            </Box>
            <Typography variant="h6" sx={{ mt: 4, color: theme.palette.secondary.main, letterSpacing: '0.1em', fontWeight: 600 }}>
              SYNTHESIZING STORYLINES
            </Typography>
            <Typography variant="caption" sx={{ mt: 1, color: alpha('#fff', 0.5) }}>
              CONNECTING DISPARATE SIGNALS
            </Typography>
          </motion.div>
        )}

        {phase === 'monitor' && (
          <motion.div
            key="monitor"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box sx={{ position: 'relative', width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <motion.div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  border: `2px solid ${theme.palette.success.main}`,
                }}
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <motion.div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.success.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 0 30px ${alpha(theme.palette.success.main, 0.5)}`,
                }}
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Typography variant="h3" sx={{ color: '#000', fontWeight: 'bold' }}>
                  A
                </Typography>
              </motion.div>
            </Box>
            <Typography variant="h6" sx={{ mt: 4, color: theme.palette.success.main, letterSpacing: '0.1em', fontWeight: 600 }}>
              ACTIVE MONITORING ENGAGED
            </Typography>
            <Typography variant="caption" sx={{ mt: 1, color: alpha('#fff', 0.5) }}>
              TRACKING DEVELOPMENTS IN REAL-TIME
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};
